import { useState, useEffect, useMemo, useCallback } from "react";
import { sGet } from "../../utils/staffApi";
import {
  C,
  Stat,
  BarChart,
  LoadingSpinner,
  Badge,
  Table,
  STATUS_COLORS,
  Btn,
} from "./AdminPanel";
import "../../styles/dashboard.css";

import {
  FiRefreshCw,
  FiDownload,
  FiBox,
  FiTrendingUp,
  FiTag,
  FiUsers,
  FiClock,
  FiArrowRight,
  FiAlertTriangle,
  FiChevronRight,
  FiPackage,
  FiPercent,
} from "react-icons/fi";
import { FaRupeeSign, FaTrophy } from "react-icons/fa";
import { MdMonetizationOn, MdShoppingCart } from "react-icons/md";

/* ─── tiny helpers ─────────────────────────────────────── */
const fmt = (n) => (n ?? 0).toLocaleString("en-IN");
const fmtPct = (n, t) => (t > 0 ? Math.round((n / t) * 100) : 0);

/* ─── Trend badge ──────────────────────────────────────── */
function Trend({ value, label }) {
  const up = value >= 0;
  return (
    <span className={`trend-badge ${up ? "trend-up" : "trend-down"}`}>
      <span className="trend-arrow">{up ? "↑" : "↓"}</span>
      {Math.abs(value)}% {label}
    </span>
  );
}

/* ─── Skeleton loader ──────────────────────────────────── */
function SkeletonCard({ height = 120 }) {
  return (
    <div className="skeleton-card" style={{ height }}>
      <div className="skeleton-shimmer" />
    </div>
  );
}

/* ─── Empty state ──────────────────────────────────────── */
function EmptyState({ icon: Icon, message }) {
  return (
    <div className="empty-state">
      <div className="empty-icon"><Icon /></div>
      <p>{message}</p>
    </div>
  );
}

/* ─── Section header ───────────────────────────────────── */
function SectionHeader({ icon: Icon, color, bg, title, sub, action }) {
  return (
    <div className="dash-card-header dash-card-header--between">
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div className="dash-card-icon" style={{ background: bg, color }}>
          <Icon />
        </div>
        <div>
          <div className="dash-card-title">{title}</div>
          <div className="dash-card-sub">{sub}</div>
        </div>
      </div>
      {action}
    </div>
  );
}

export default function Dashboard({ toast, navigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  /* ─── fetch ──────────────────────────────────────────── */
  const fetchData = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      try {
        const result = await sGet("/dashboard");
        setData(result);
        setLastUpdated(new Date());
      } catch (e) {
        toast?.(e.message, "error");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [toast],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ─── auto-refresh every 5 min ───────────────────────── */
  useEffect(() => {
    const id = setInterval(() => fetchData(true), 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [fetchData]);

  /* ─── ALL useMemo hooks MUST be before conditional returns ─── */
  const today = useMemo(
    () =>
      new Intl.DateTimeFormat("en-IN", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      }).format(new Date()),
    [],
  );

  const lastUpdatedStr = useMemo(() => {
    if (!lastUpdated) return null;
    return new Intl.DateTimeFormat("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(lastUpdated);
  }, [lastUpdated]);

  // Extract monthlySales for useMemo calculations
  const monthlySales = data?.monthlySales ?? [];

  const revenueChange = useMemo(() => {
    if (monthlySales.length < 2) return null;
    const last = monthlySales[monthlySales.length - 1]?.revenue ?? 0;
    const prev = monthlySales[monthlySales.length - 2]?.revenue ?? 0;
    if (prev === 0) return null;
    return Math.round(((last - prev) / prev) * 100);
  }, [monthlySales]);

  const ordersChange = useMemo(() => {
    if (monthlySales.length < 2) return null;
    const last = monthlySales[monthlySales.length - 1]?.orders ?? 0;
    const prev = monthlySales[monthlySales.length - 2]?.orders ?? 0;
    if (prev === 0) return null;
    return Math.round(((last - prev) / prev) * 100);
  }, [monthlySales]);

  /* ─── export ─────────────────────────────────────────── */
  const handleExportAll = useCallback(() => {
    if (!data) return;
    const rows = [];
    rows.push(`"Dashboard Export — ${new Date().toLocaleString("en-IN")}"`);
    rows.push("");
    rows.push('"=== SUMMARY ==="');
    rows.push('"Metric","Value"');
    rows.push(`"Total Orders","${data.stats?.totalOrders ?? 0}"`);
    rows.push(`"Total Revenue","₹${fmt(data.stats?.totalRevenue)}"`);
    rows.push(`"Total Products","${data.stats?.totalProducts ?? 0}"`);
    rows.push(`"Total Customers","${data.stats?.totalUsers ?? 0}"`);
    rows.push("");
    rows.push('"=== RECENT ORDERS ==="');
    rows.push('"Order ID","Customer","Amount","Status","Date"');
    (data.recentOrders ?? []).forEach((o) =>
      rows.push(
        `"${o.orderId}","${o.user?.name ?? "Guest"}","₹${fmt(o.totalAmount)}","${o.orderStatus}","${new Date(o.createdAt).toLocaleDateString("en-IN")}"`,
      ),
    );
    rows.push("");
    rows.push('"=== ORDER STATUS BREAKDOWN ==="');
    rows.push('"Status","Count"');
    (data.ordersByStatus ?? []).forEach((s) =>
      rows.push(`"${s._id}","${s.count}"`),
    );
    rows.push("");
    rows.push('"=== TOP SELLING PRODUCTS ==="');
    rows.push('"Product","Units Sold","Revenue"');
    (data.topProducts ?? []).forEach((p) =>
      rows.push(`"${p.name}","${p.sales}","₹${fmt(p.revenue)}"`),
    );
    if ((data.lowStock ?? []).length > 0) {
      rows.push("");
      rows.push('"=== LOW STOCK ALERTS ==="');
      rows.push('"Product","Stock Left"');
      data.lowStock.forEach((i) => rows.push(`"${i.name}","${i.stock}"`));
    }

    const blob = new Blob([rows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `dashboard_${new Date().toISOString().split("T")[0]}.csv`;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    toast?.("Dashboard exported successfully", "success");
  }, [data, toast]);

  /* ─── loading skeleton (AFTER all hooks) ───────────────────────────────── */
  if (loading) {
    return (
      <div className="dash">
        <div className="dash-header">
          <div>
            <div className="skeleton-line" style={{ width: 160, height: 28 }} />
            <div
              className="skeleton-line"
              style={{ width: 240, height: 14, marginTop: 6 }}
            />
          </div>
        </div>
        <div className="dash-kpi-grid">
          {[...Array(4)].map((_, i) => (
            <SkeletonCard key={i} height={100} />
          ))}
        </div>
        <div className="dash-charts-grid">
          <SkeletonCard height={280} />
          <SkeletonCard height={280} />
        </div>
      </div>
    );
  }

  const {
    stats = {},
    recentOrders = [],
    ordersByStatus = [],
    monthlySales: finalMonthlySales = [],
    topProducts = [],
    lowStock = [],
  } = data ?? {};

  const totalStatusOrders = ordersByStatus.reduce((s, x) => s + x.count, 0);
  const maxRevenue = Math.max(...finalMonthlySales.map((m) => m.revenue ?? 0), 1);

  return (
    <div className="dash">
      {/* ── Header ───────────────────────────────────────── */}
      <div className="dash-header">
        <div>
          <h2 className="dash-title">Overview</h2>
          <p className="dash-date">
            {today}
            {lastUpdatedStr && (
              <span className="dash-last-updated">
                &nbsp;· Updated {lastUpdatedStr}
              </span>
            )}
          </p>
        </div>
        <div className="dash-actions">
          <Btn
            variant="ghost"
            size="sm"
            onClick={() => fetchData(true)}
            disabled={refreshing}
            title="Refresh dashboard data"
            aria-label="Refresh dashboard data"
          >
            <FiRefreshCw className={refreshing ? "spin" : ""} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </Btn>
          <Btn
            variant="primary"
            size="sm"
            onClick={handleExportAll}
            title="Export all dashboard data as CSV"
            aria-label="Export dashboard data to CSV"
          >
            <FiDownload /> Export CSV
          </Btn>
        </div>
      </div>

      {/* ── KPI strip ────────────────────────────────────── */}
      <div className="dash-kpi-grid" role="region" aria-label="Key metrics">
        <Stat
          icon={<FiBox />}
          label="Total Orders"
          value={fmt(stats?.totalOrders)}
          color="#6366f1"
          sub={
            ordersChange !== null ? (
              <Trend value={ordersChange} label="vs last month" />
            ) : (
              "All time volume"
            )
          }
        />
        <Stat
          icon={<FaRupeeSign />}
          label="Total Revenue"
          value={`₹${fmt(stats?.totalRevenue)}`}
          color="#10b981"
          sub={
            revenueChange !== null ? (
              <Trend value={revenueChange} label="vs last month" />
            ) : (
              "Excluding cancelled"
            )
          }
        />
        <Stat
          icon={<FiTag />}
          label="Products"
          value={fmt(stats?.totalProducts)}
          color="#f59e0b"
          sub={
            lowStock.length > 0 ? (
              <span className="kpi-alert">
                <FiAlertTriangle /> {lowStock.length} low stock
              </span>
            ) : (
              "Active catalogue"
            )
          }
        />
        <Stat
          icon={<FiUsers />}
          label="Customers"
          value={fmt(stats?.totalUsers)}
          color="#0ea5e9"
          sub="Registered accounts"
        />
      </div>

      {/* ── Charts row ───────────────────────────────────── */}
      <div className="dash-charts-grid">
        <div className="dash-card">
          <SectionHeader
            icon={MdMonetizationOn}
            color="#6366f1"
            bg="rgba(99,102,241,0.1)"
            title="Monthly Revenue"
            sub="Last 12 months"
          />
          {finalMonthlySales.length === 0 ? (
            <EmptyState icon={MdMonetizationOn} message="No revenue data yet" />
          ) : (
            <BarChart
              data={finalMonthlySales}
              labelKey="_id"
              valueKey="revenue"
              color="#6366f1"
            />
          )}
        </div>

        <div className="dash-card">
          <SectionHeader
            icon={MdShoppingCart}
            color="#f59e0b"
            bg="rgba(245,158,11,0.1)"
            title="Monthly Orders"
            sub="Last 12 months"
          />
          {finalMonthlySales.length === 0 ? (
            <EmptyState icon={MdShoppingCart} message="No order data yet" />
          ) : (
            <BarChart
              data={finalMonthlySales}
              labelKey="_id"
              valueKey="orders"
              color="#f59e0b"
            />
          )}
        </div>
      </div>

      {/* ── Operations row ───────────────────────────────── */}
      <div className="dash-ops-layout">
        {/* Recent Orders */}
        <div className="dash-card dash-ops-main">
          <SectionHeader
            icon={FiClock}
            color="#0ea5e9"
            bg="rgba(14,165,233,0.1)"
            title="Recent Orders"
            sub="Latest transactions"
            // action={
            //   <Btn
            //     variant="ghost"
            //     size="sm"
            //     onClick={() => navigate?.("/orders")}
            //     aria-label="View all orders"
            //   >
            //     View All <FiChevronRight />
            //   </Btn>
            // }
          />
          {recentOrders.length === 0 ? (
            <EmptyState icon={FiBox} message="No orders yet" />
          ) : (
            <Table
              columns={[
                {
                  key: "orderId",
                  label: "Order ID",
                  render: (v) => (
                    <button
                      className="dash-order-id dash-order-link"
                      onClick={() => navigate?.(`/orders/${v}`)}
                      title={`View order #${v}`}
                      aria-label={`View order #${v}`}
                    >
                      #{v}
                    </button>
                  ),
                },
                {
                  key: "user",
                  label: "Customer",
                  render: (v) => (
                    <div className="dash-customer">
                      <div
                        className="dash-customer-avatar"
                        aria-hidden="true"
                        title={v?.name || "Guest"}
                      >
                        {(v?.name || "G").charAt(0).toUpperCase()}
                      </div>
                      <span className="dash-customer-name">
                        {v?.name || "Guest"}
                      </span>
                    </div>
                  ),
                },
                {
                  key: "totalAmount",
                  label: "Amount",
                  render: (v) => (
                    <span className="dash-amount">₹{fmt(v)}</span>
                  ),
                },
                {
                  key: "orderStatus",
                  label: "Status",
                  render: (v) => <Badge status={v} />,
                },
                {
                  key: "createdAt",
                  label: "Date",
                  render: (v) => (
                    <time
                      className="dash-date-cell"
                      dateTime={new Date(v).toISOString()}
                      title={new Date(v).toLocaleDateString("en-IN", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    >
                      {new Date(v).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "2-digit",
                      })}
                    </time>
                  ),
                },
              ]}
              data={recentOrders.slice(0, 8)}
              loading={false}
            />
          )}
        </div>

        {/* Sidebar */}
        <div className="dash-ops-sidebar">
          {/* Fulfillment Status */}
          <div className="dash-card">
            <SectionHeader
              icon={FiTrendingUp}
              color="#10b981"
              bg="rgba(16,185,129,0.1)"
              title="Fulfillment"
              sub="Order status breakdown"
            />
            {ordersByStatus.length === 0 ? (
              <EmptyState icon={FiPercent} message="No status data" />
            ) : (
              <div
                className="dash-status-list"
                role="list"
                aria-label="Order fulfillment status breakdown"
              >
                {ordersByStatus.map((s) => {
                  const colors = STATUS_COLORS[s._id] ?? {};
                  const pct = fmtPct(s.count, totalStatusOrders);
                  return (
                    <div key={s._id} className="dash-status-item" role="listitem">
                      <div className="dash-status-row">
                        <span className="dash-status-name">{s._id}</span>
                        <span className="dash-status-count">
                          {fmt(s.count)}
                          <span
                            className="dash-status-pct"
                            aria-label={`${pct} percent`}
                          >
                            {pct}%
                          </span>
                        </span>
                      </div>
                      <div
                        className="dash-progress-track"
                        role="progressbar"
                        aria-valuenow={pct}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label={`${s._id}: ${pct}%`}
                      >
                        <div
                          className="dash-progress-fill"
                          style={{
                            width: `${pct}%`,
                            background: colors.text || "#6366f1",
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Low Stock Alert */}
          {lowStock.length > 0 && (
            <div
              className="dash-card dash-alert-card"
              role="alert"
              aria-live="polite"
              aria-label={`Low stock alert: ${lowStock.length} products need restocking`}
            >
              <div className="dash-alert-pulse" aria-hidden="true" />
              <SectionHeader
                icon={FiAlertTriangle}
                color="var(--danger)"
                bg="rgba(240,68,56,0.1)"
                title="Low Stock"
                sub={`${lowStock.length} product${lowStock.length !== 1 ? "s" : ""} need restocking`}
                // action={
                //   <Btn
                //     variant="ghost"
                //     size="sm"
                //     onClick={() => navigate?.("/products?filter=low-stock")}
                //     aria-label="View all low stock products"
                //   >
                //     Manage <FiChevronRight />
                //   </Btn>
                // }
              />
              <div className="dash-alert-list" role="list">
                {lowStock.slice(0, 5).map((p) => (
                  <div key={p._id} className="dash-alert-item" role="listitem">
                    <button
                      className="dash-alert-name dash-alert-link"
                      onClick={() => navigate?.(`/products/${p._id}`)}
                      title={`View product: ${p.name}`}
                    >
                      <FiPackage
                        style={{
                          marginRight: 6,
                          flexShrink: 0,
                          opacity: 0.5,
                          fontSize: 12,
                        }}
                        aria-hidden="true"
                      />
                      {p.name}
                    </button>
                    <span
                      className="dash-alert-qty"
                      aria-label={`${p.stock} units remaining`}
                    >
                      {p.stock} left
                    </span>
                  </div>
                ))}
              </div>
              {lowStock.length > 5 && (
                <button
                  className="dash-alert-more"
                  onClick={() => navigate?.("/products?filter=low-stock")}
                  aria-label={`View ${lowStock.length - 5} more low stock items`}
                >
                  +{lowStock.length - 5} more items →
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Top Products ──────────────────────────────────── */}
      {topProducts.length > 0 && (
        <div className="dash-card">
          <SectionHeader
            icon={FaTrophy}
            color="#f59e0b"
            bg="rgba(245,158,11,0.1)"
            title="Top Selling Products"
            sub="Ranked by revenue & volume"
            // action={
            //   <Btn
            //     variant="ghost"
            //     size="sm"
            //     onClick={() => navigate?.("/products")}
            //     aria-label="View all products"
            //   >
            //     View All <FiChevronRight />
            //   </Btn>
            // }
          />
          <Table
            columns={[
              {
                key: "_id",
                label: "#",
                render: (_, __, idx) => (
                  <span
                    className={`dash-rank ${idx === 0 ? "rank-gold" : idx === 1 ? "rank-silver" : idx === 2 ? "rank-bronze" : ""}`}
                    aria-label={`Rank ${idx + 1}`}
                  >
                    {idx + 1}
                  </span>
                ),
              },
              {
                key: "name",
                label: "Product",
                render: (v, row) => (
                  <button
                    className="dash-product-name dash-product-link"
                    onClick={() => navigate?.(`/products/${row._id}`)}
                    title={`View ${v}`}
                  >
                    {v}
                  </button>
                ),
              },
              {
                key: "sales",
                label: "Units Sold",
                render: (v) => (
                  <span className="dash-units">
                    <span
                      className="dash-units-bar"
                      style={{ width: `${Math.min(v * 4, 80)}px` }}
                      aria-hidden="true"
                    />
                    {fmt(v)}
                  </span>
                ),
              },
              {
                key: "revenue",
                label: "Revenue",
                render: (v, _, idx) => (
                  <div className="dash-revenue-cell">
                    <span className="dash-revenue">₹{fmt(v)}</span>
                    <div className="dash-revenue-bar-track" aria-hidden="true">
                      <div
                        className="dash-revenue-bar-fill"
                        style={{
                          width: `${Math.round((v / maxRevenue) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                ),
              },
            ]}
            data={topProducts}
            loading={false}
          />
        </div>
      )}

      {/* ── Footer ───────────────────────────────────────── */}
      <footer className="dash-footer" role="contentinfo">
        <span>
          Data reflects live store activity.
          {lastUpdatedStr && ` Last synced at ${lastUpdatedStr}.`}
        </span>
        <div className="dash-footer-links">
          <button
            className="dash-footer-link"
            onClick={() => navigate?.("/reports")}
          >
            Full Reports
          </button>
          <button
            className="dash-footer-link"
            onClick={() => navigate?.("/settings")}
          >
            Settings
          </button>
          <button className="dash-footer-link" onClick={handleExportAll}>
            Export CSV
          </button>
        </div>
      </footer>
    </div>
  );
}